import { serialize } from 'object-to-formdata';
import request from '../../utils/request';
import type { PayBasis, Result, UserView } from '../../models/general';

/**
 * Hourly pay — per-employee, effective-dated, ENCRYPTED rate history (docs/hourly-pay-spec.md §3.2).
 * `api/UserHourlyRate/{userId}`, HR ONLY. The rate is salary data: never call this from a surface an
 * Admin, Manager or the employee can reach.
 *
 * Routes as the backend lane landed them (Controllers/Payroll/ShiftPayApprovalsController.cs):
 *   GET    UserHourlyRate/{userId}        → rate views
 *   POST   UserHourlyRate/{userId}        { rate, currencyId?, effectiveStart, effectiveStop? }
 *   PUT    UserHourlyRate/{userId}/{id}   same body; omitted fields keep their values
 *   DELETE UserHourlyRate/{userId}/{id}   → true (archives)
 * `effectiveStop` is sent as '' (never omitted) for "open-ended", because on PUT an omitted field
 * keeps its stored value and a row could never be re-opened.
 */

export interface UserHourlyRateView {
  id: number;
  userId: string;
  /** Decrypted decimal per hour. */
  rate: number;
  currencyId: number;
  currencyCode?: string;
  /** `yyyy-MM-dd`, local date. */
  effectiveStart: string;
  /** `yyyy-MM-dd`; absent/null = open-ended. Ranges for one user never overlap (400 otherwise). */
  effectiveStop?: string | null;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
}

export interface UserHourlyRateInput {
  rate: number;
  /** v1: must equal the company currency (400 otherwise). Optional: the server defaults it. */
  currencyId?: number;
  effectiveStart: string;
  /** `yyyy-MM-dd`, or '' for open-ended. */
  effectiveStop: string;
}

export async function getUserHourlyRates(userId: string): Promise<Result<UserHourlyRateView[]>> {
  const response = await request.get<Result<UserHourlyRateView[]>>(
    `UserHourlyRate/${encodeURIComponent(userId)}`,
  );
  return response.data;
}

export async function addUserHourlyRate(
  userId: string,
  input: UserHourlyRateInput,
): Promise<Result<UserHourlyRateView>> {
  const response = await request.post<Result<UserHourlyRateView>>(
    `UserHourlyRate/${encodeURIComponent(userId)}`,
    input,
  );
  return response.data;
}

export async function updateUserHourlyRate(
  userId: string,
  id: number,
  input: UserHourlyRateInput,
): Promise<Result<UserHourlyRateView>> {
  const response = await request.put<Result<UserHourlyRateView>>(
    `UserHourlyRate/${encodeURIComponent(userId)}/${id}`,
    input,
  );
  return response.data;
}

export async function deleteUserHourlyRate(userId: string, id: number): Promise<Result<boolean>> {
  const response = await request.delete<Result<boolean>>(
    `UserHourlyRate/${encodeURIComponent(userId)}/${id}`,
  );
  return response.data;
}

/**
 * §7.2 — "`PayBasis` goes through the existing user-profile update, HR only."
 *
 * 🔴 `POST account/{id}` is the CO-248 PARTIAL-UPDATE form endpoint: a key that is NOT posted is left
 * alone, a key that IS posted is written. So this sends `payBasis` plus ONLY the identity fields the
 * HR edit path requires (FirstName/Role gate it, and the DTO's non-nullable strings are implicitly
 * required by model binding), each carrying the value just read from `GET account/{id}` — i.e. a
 * no-op for every field but `payBasis`. Nothing else (managers, dates, IBAN…) is ever put on the wire.
 */
export async function updateUserPayBasis(user: UserView, payBasis: PayBasis): Promise<Result<UserView>> {
  const body: Record<string, string> = {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    fullName: user.fullName ?? '',
    email: user.email ?? '',
    userName: user.userName ?? '',
    role: user.role ?? '',
    payBasis,
  };
  const response = await request.post<Result<UserView>>(
    `account/${encodeURIComponent(user.id)}`,
    serialize(body),
  );
  return response.data;
}
